import { ClipboardIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { cn } from '@/lib/utils.js';

import { notify } from '@/lib/notify.js';

import { useCredentialAction } from '../../hooks/useCredentialAction.js';
import * as api from '../../services/integration-api.js';

import { ConnectionChoiceSelect } from './ConnectionChoiceSelect.js';
import { useConnectorScope } from './connector-scope.js';

/**
 * Zephyr Scale Cloud is api-key only (a per-user bearer token generated in
 * Jira → Profile → Zephyr Scale API keys). Unlike TestRail there is no
 * per-org instance URL to type: SmartBear runs exactly two Cloud API bases
 * (US and EU) and identity allowlists exactly those two hosts, so the form
 * exposes a fixed Region choice instead of a free-text URL. US is the
 * server-side default — the EU base is only sent when explicitly chosen.
 */

interface FormErrors {
  apiKey?: string;
  submit?: string;
}

function validate(values: api.ZephyrCredentialsPayload): FormErrors {
  const errors: FormErrors = {};
  if (values.apiKey.length < 8) {
    errors.apiKey = 'API token looks too short';
  }
  return errors;
}

export function ZephyrCredentialsForm({
  onSaved,
  onCancel,
  details,
  explainUnavailableActions = false,
  onBusyChange,
}: {
  onSaved: () => void;
  onCancel: () => void;
  /**
   * What the new connection should be called and when the agent should reach for it, plus which lane it
   * lands in. Absent on a first connection of the provider — there is nothing to tell it apart from.
   */
  details?: api.ConnectionCreateDetails;
  explainUnavailableActions?: boolean;
  onBusyChange?: (busy: boolean) => void;
}): JSX.Element {
  const [values, setValues] = useState<api.ZephyrCredentialsPayload>(() => ({
    apiKey: '',
    region: 'us',
  }));
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const { workspaceId } = useConnectorScope();
  const action = useCredentialAction(
    explainUnavailableActions,
    `${workspaceId ?? ''}:${details?.connectionId ?? 'new'}`,
    onBusyChange,
  );

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setValues((v) => ({ ...v, apiKey: e.target.value }));
    if (errors.apiKey) setErrors((es) => ({ ...es, apiKey: undefined }));
  };

  // Read the clipboard and stuff the value into the API token field. Kept
  // as a button alongside the input because users have reported the
  // native Cmd/Ctrl+V occasionally not landing in this specific input
  // (suspected interaction with the surrounding Radix Dialog focus scope
  // / password-input quirks). The Clipboard API ask runs on a user
  // gesture, so the browser permission prompt is fine.
  const handlePasteFromClipboard = async (): Promise<void> => {
    if (action.blocked()) return;
    setPasteError(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setValues((v) => ({ ...v, apiKey: text.trim() }));
      if (errors.apiKey) setErrors((es) => ({ ...es, apiKey: undefined }));
    } catch {
      setPasteError('Clipboard access denied. Paste manually with Cmd/Ctrl+V.');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (action.blocked()) return;
    const v = validate(values);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      if (explainUnavailableActions)
        notify.error(
          Object.values(v)[0] ?? 'Complete the required connection fields.',
        );
      return;
    }
    const ticket = action.begin();
    if (!ticket) return;
    setSubmitting(true);
    setErrors({});
    try {
      await api.saveZephyrCredentials(values, workspaceId, details);
      action.complete(ticket, onSaved);
    } catch (err) {
      if (!action.isCurrent(ticket)) return;
      const message =
        err instanceof Error ? err.message : 'Failed to save credentials';
      setErrors({ submit: message });
    } finally {
      if (action.isCurrent(ticket)) setSubmitting(false);
      action.end(ticket);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-3"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="zephyr-region" className="text-xs">
          Region
        </Label>

        {explainUnavailableActions ? (
          <ConnectionChoiceSelect
            id="zephyr-region"
            value={values.region}
            options={[
              { value: 'us', label: 'US (default)' },
              { value: 'eu', label: 'EU' },
            ]}
            blocked={action.blocked}
            onValueChange={(region) =>
              setValues((current) => ({
                ...current,
                region: region as api.ZephyrRegion,
              }))
            }
          />
        ) : (
          <select
            id="zephyr-region"
            value={values.region}
            onChange={(e) =>
              setValues((v) => ({
                ...v,
                region: e.target.value as api.ZephyrRegion,
              }))
            }
            className={cn(
              'flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-elev-1 transition-[color,box-shadow] outline-none',
              'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <option value="us">US (default)</option>
            <option value="eu">EU</option>
          </select>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="zephyr-apiKey" className="text-xs">
          API token
        </Label>
        <div className="flex items-stretch gap-1.5">
          <div className="relative flex-1">
            <Input
              id="zephyr-apiKey"
              type={showKey ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="Zephyr Scale API token"
              value={values.apiKey}
              onChange={handleKeyChange}
              aria-invalid={Boolean(errors.apiKey)}
              aria-describedby={
                errors.apiKey ? 'zephyr-apiKey-err' : 'zephyr-apiKey-help'
              }
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowKey((s) => !s)}
              aria-label={showKey ? 'Hide token' : 'Show token'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
            >
              {showKey ? (
                <EyeOffIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : (
                <EyeIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard()}
            aria-label="Paste from clipboard"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <ClipboardIcon
              className="size-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Paste
          </button>
        </div>
        {pasteError && (
          <p className="text-[11px] text-destructive">{pasteError}</p>
        )}
        {errors.apiKey ? (
          <p id="zephyr-apiKey-err" className="text-xs text-destructive">
            {errors.apiKey}
          </p>
        ) : (
          <p
            id="zephyr-apiKey-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            In Jira, open your{' '}
            <span className="font-medium">Profile → Zephyr Scale API keys</span>{' '}
            and create a token. Paste it here.
          </p>
        )}
      </div>
      {errors.submit && (
        <p className="text-xs text-destructive">{errors.submit}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => action.cancel(onCancel)}
          disabled={!explainUnavailableActions && submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!explainUnavailableActions && submitting}
        >
          {submitting ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}
